import React, { useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { brand } from "../../../theme/businessFlowsTheme";
import { Refusal } from "../../workspaces/WorkspaceUi";
import { clearExplorerRootObject, getExplorerConfig, listContentObjects, setExplorerRootObject } from "../../../actions";

// Where the relationship explorer opens (the existing ExplorerStart choice).
export default function ExplorerStartCard({ database, types = [], mayManage, apply, askReset }) {
  const [explorer, setExplorer] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState([]);
  const reads = useRef(0);

  const labelOf = (name) => types.find((t) => t.objectType === name)?.label || name;

  // Only the newest read lands: a late answer for another database is dropped.
  const load = useCallback(async () => {
    if (!database) return;
    const ticket = ++reads.current;
    setExplorer(null);
    setLoadError("");
    const res = await getExplorerConfig(database);
    if (ticket !== reads.current) return;
    if (res?.success) setExplorer(res.content || {});
    else {
      setExplorer({});
      setLoadError(res?.msg || "Couldn't read where the explorer starts. Try again in a moment.");
    }
  }, [database]);

  useEffect(() => {
    setQuery("");
    setCandidates([]);
    load();
    const counter = reads;
    return () => {
      counter.current += 1;
    };
  }, [load]);

  useEffect(() => {
    if (!database || query.trim().length < 2) {
      setCandidates([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      const res = await listContentObjects(database, { query: query.trim(), limit: 12 });
      if (!cancelled) setCandidates(res?.success ? res.content : []);
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [database, query]);

  const choose = async (item) => {
    const result = await apply(() => setExplorerRootObject(database, item.guid), `The explorer now starts at ${item.name}.`);
    if (result?.ok) {
      setQuery("");
      setCandidates([]);
      await load();
    }
  };

  const reset = () =>
    askReset({
      title: "Start the explorer at its default?",
      text: "The explorer opens at the root of this database again, or at its best-connected object when there is no root.",
      request: async () => {
        const res = await clearExplorerRootObject(database);
        if (res?.success) load();
        return res;
      },
      message: "The explorer starts at its default again.",
    });

  const shown = explorer?.chosen
    ? explorer.chosen.missing
      ? { name: "The chosen object is gone", note: "It was removed from this database. Choose another, or go back to the default." }
      : { name: explorer.chosen.name, note: "Chosen by an admin" }
    : explorer?.defaultGuid
    ? { name: explorer.defaultName || "Database root", note: "Default: the root of this database" }
    : explorer?.automatic
    ? { name: explorer.automatic.name, note: "Default: the best-connected object" }
    : { name: "Nowhere yet", note: "This database has no objects to start from." };

  return (
    <Box sx={{ py: 1.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 650 }}>
        Explorer starts at
      </Typography>
      {explorer === null ? (
        <Skeleton height={56} />
      ) : loadError ? (
        <Refusal
          sx={{ mt: 1 }}
          action={
            <Button size="small" onClick={load} sx={{ flexShrink: 0 }}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Refusal>
      ) : (
        <Box sx={{ mt: 1, p: 1.5, borderRadius: 3, border: `1px solid ${brand.line}`, bgcolor: brand.shell }}>
          <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>
            {shown.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {shown.note}
          </Typography>
        </Box>
      )}
      {mayManage && !loadError && (
        <>
          <TextField
            size="small"
            fullWidth
            sx={{ mt: 1.5, width: { xs: "100%", sm: 320 } }}
            placeholder="Search for another object"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            inputProps={{ "aria-label": "Search for another object to start at" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
          />
          {candidates.length > 0 && (
            <Stack spacing={0.5} sx={{ mt: 1, maxHeight: 240, overflowY: "auto" }}>
              {candidates.map((item) => (
                <Box
                  key={item.guid}
                  role="button"
                  tabIndex={0}
                  aria-label={`Start at ${item.name} (${labelOf(item.type)})`}
                  onClick={() => choose(item)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      choose(item);
                    }
                  }}
                  sx={{
                    p: 1.25,
                    borderRadius: 2.5,
                    border: `1px solid ${brand.line}`,
                    cursor: "pointer",
                    "&:hover": { borderColor: brand.inkSoft, bgcolor: brand.canvas },
                    "&:focus-visible": { outline: `2px solid ${brand.ink}` },
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
                    {item.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {labelOf(item.type)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
          {explorer?.chosen && (
            <Button sx={{ mt: 1 }} startIcon={<RestartAltRoundedIcon />} onClick={reset}>
              Back to the default
            </Button>
          )}
        </>
      )}
    </Box>
  );
}
